import { useRef } from 'react'
import type React from 'react'
import type { WireColor } from '@/api/types/color'
import { tintedPaletteHex } from '@/lib/colors/resolve-palette-color'
import { isNoneColor } from '@/lib/colors/fill-paint'
import { useColorPlacementOptional } from '@/contexts/color-placement-context'

/**
 * Threshold-based pointer drag for a color swatch. Below the threshold, a normal click fires
 * and the drag never starts (so the color popover still works). Above it, the drag session
 * takes over.
 *
 * List/grid swatches pass no tint (defaults to 100 — the full color on the chip). The header
 * swatch passes the live tint so the drag carries the exact fill the user has dialled in.
 *
 * `ghostRectElRef` lets a caller supply an inner element whose rect sizes the drag ghost.
 * Useful in list rows where the pointer handlers live on the full-width button but the ghost
 * should match the small color chip inside it.
 *
 * `onDragStart` runs once the threshold is crossed, just before the drag session takes over, so
 * the popover can mark the swatch selected while it's being dragged.
 */
export function useColorSwatchDrag(
  color: WireColor | null,
  tint = 100,
  ghostRectElRef?: React.RefObject<HTMLElement | null>,
  onDragStart?: () => void
) {
  const colorPlacement = useColorPlacementOptional()
  const pendingRef = useRef<{
    clientX: number
    clientY: number
    rect: DOMRect
    pointerId: number
    pointerType: string
    el: HTMLElement
  } | null>(null)

  const isNone = color != null && isNoneColor(color)
  // 'transparent' is valid CSS and gives the ghost an empty look that communicates "remove fill".
  const hex = isNone
    ? 'transparent'
    : color
      ? (tintedPaletteHex(color, tint) ?? color.hexValue)
      : ''

  function onPointerDown(e: React.PointerEvent<HTMLElement>) {
    if (!colorPlacement || !color) return

    const rectEl = ghostRectElRef?.current ?? e.currentTarget

    pendingRef.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      rect: rectEl.getBoundingClientRect(),
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      el: e.currentTarget,
    }
  }

  function onPointerMove(e: React.PointerEvent<HTMLElement>) {
    const pending = pendingRef.current

    if (!pending || !colorPlacement || !color) return

    if (Math.abs(e.clientX - pending.clientX) <= 5 && Math.abs(e.clientY - pending.clientY) <= 5) {
      return
    }

    pendingRef.current = null
    onDragStart?.()
    colorPlacement.beginPointerDrag(
      { color, tint, hex },
      pending.clientX,
      pending.clientY,
      pending.rect,
      pending.pointerId,
      pending.pointerType,
      pending.el
    )
  }

  function onPointerUp() {
    pendingRef.current = null
  }

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp }
}
